import { useMutation, useQuery } from '@apollo/client';
import { AdBanner } from '@techminds/ad-banner';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  getCookie,
  setCookie,
} from '@flight-workspace/common-client/src/util/cookie';
import { getPushDeviceToken } from '@flight-workspace/common-client/src/util/naverapp';
import config from 'config/index';
import GET_ADS_CONSENT from 'hooks/receiveAds.gql';
import UPDATE_ADS_CONSENT from 'hooks/updateReceiveAds.gql';
import useTravelUser from 'hooks/useTravelUser';
import {
  GetReceiveAdsQuery,
  ReceiveAdsAssentLinkMutation,
  ReceiveAdsAssentLinkMutationVariables,
} from 'types/apollo';
import styles from './lineBanner.module.scss';

const MAX_WIDTH = 880;
const ADS_CONSENT_TOGGLE_WIDTH = 34;
const cx = classNames.bind(styles);
const AD_SLOT_SOURCE = 'line-banner';
const AD_TYPE_COOKIE_SUFFIX = '_BANNER';
const commonAdSlotInfo = {
  adUnitId: 'tm_travel_flight_line_banner',
  uct: 'KR',
};

enum AdType {
  ADS_CONSENT = 'ADS_CONSENT',
  DEFAULT = 'DEFAULT',
  HOTEL_DISCOUNT_COUPON = 'HOTEL_DISCOUNT_COUPON',
}

const LineBanner = () => {
  const hasAlertShown = useRef(false);
  const [isOpen, setIsOpen] = useState(true);
  const [isAdLoaded, setIsAdLoaded] = useState(false);
  const [isToggleButtonChecked, setIsToggleButtonChecked] = useState(false);
  const [selectedAd, setSelectedAd] = useState<AdType | undefined>(undefined);
  const { loading: loadingTravelUser, nickName, coupons } = useTravelUser();
  const { loading: loadingReceiveAds, data } =
    useQuery<GetReceiveAdsQuery>(GET_ADS_CONSENT);
  const [bannerContentWidth, setBannerContentWidth] = useState<
    number | undefined
  >(undefined);

  const adSlotInfo = useMemo(
    () => ({
      ...commonAdSlotInfo,
      ...(selectedAd
        ? { customParam: { adType: selectedAd?.toLocaleLowerCase() } }
        : {}),
    }),
    [selectedAd],
  );

  const handleLineBannerClose = () => {
    setIsOpen(false);
    if (selectedAd) {
      setCookie(selectedAd + AD_TYPE_COOKIE_SUFFIX, 'true', {
        expires: dayjs().add(1, 'day').startOf('day').toDate().toUTCString(),
        path: '/',
      });
    }
  };

  const [updateAdsConsent] = useMutation<
    ReceiveAdsAssentLinkMutation,
    ReceiveAdsAssentLinkMutationVariables
  >(UPDATE_ADS_CONSENT);

  const updateEventAlert = async () => {
    const deviceToken = await getPushDeviceToken();
    const { data } = await updateAdsConsent({
      variables: { deviceToken, updateAssent: true },
    });

    if (data?.updateReceiveAds?.isAssent && !hasAlertShown.current) {
      hasAlertShown.current = true;
      setIsToggleButtonChecked(true);
      setTimeout(() => {
        alert(
          `${dayjs().format(
            'YYYY년 M월 D일',
          )} 네이버 여행상품에 요청하신 광고성 정보 수신 동의가 완료되었습니다.`,
        );
        handleLineBannerClose();
      }, 50);
    }
  };

  const adSlotHostMeta = useMemo(() => ({ origin: config.flight }), [config]);

  const handleLoadAd = useCallback(() => {
    setIsAdLoaded(true);
  }, []);

  const handleAdMessage = useCallback(
    (e: MessageEvent) => {
      if (
        e.origin !== adSlotHostMeta.origin ||
        e.data?.source !== AD_SLOT_SOURCE ||
        !e.data?.payload?.lineBannerWidth
      ) {
        return;
      }

      const { lineBannerWidth } = e.data.payload;

      if (0 < lineBannerWidth) {
        const adsConsentToggleWidth =
          selectedAd === AdType.ADS_CONSENT ? ADS_CONSENT_TOGGLE_WIDTH : 0;
        setBannerContentWidth(
          Math.min(
            e.data.payload.lineBannerWidth + adsConsentToggleWidth,
            MAX_WIDTH,
          ),
        );
      }
    },
    [adSlotHostMeta, selectedAd],
  );

  useEffect(() => {
    const adTypeFromCookies = () => {
      if (
        !getCookie(AdType.HOTEL_DISCOUNT_COUPON + AD_TYPE_COOKIE_SUFFIX) &&
        coupons?.length
      ) {
        return AdType.HOTEL_DISCOUNT_COUPON;
      }
      if (
        !getCookie(AdType.ADS_CONSENT + AD_TYPE_COOKIE_SUFFIX) &&
        nickName &&
        !data?.receiveAds?.isAssent
      ) {
        return AdType.ADS_CONSENT;
      }
      if (!getCookie(AdType.DEFAULT + AD_TYPE_COOKIE_SUFFIX)) {
        return AdType.DEFAULT;
      }
      return undefined;
    };

    setSelectedAd(adTypeFromCookies());
  }, [getCookie, coupons, nickName, data?.receiveAds?.isAssent]);

  if (!isOpen || loadingTravelUser || loadingReceiveAds || !selectedAd) {
    return null;
  }

  return (
    <div
      className={cx('LineBanner')}
      aria-busy={!isAdLoaded}
    >
      <div className={cx('container')}>
        <div
          className={cx('banner_inner')}
          style={{ width: bannerContentWidth }}
        >
          <AdBanner
            adSlotInfo={adSlotInfo}
            onAdLoaded={handleLoadAd}
            hostMeta={adSlotHostMeta}
            onPostMessage={handleAdMessage}
            className={cx(
              'banner_wrapper',
              selectedAd === AdType.ADS_CONSENT && 'ads_consent',
            )}
          />
          {selectedAd === AdType.ADS_CONSENT && (
            <label className={cx('checkbox')} onClick={updateEventAlert}>
              <input type="checkbox" checked={isToggleButtonChecked} readOnly />
            </label>
          )}
          <button
            className={cx('close')}
            type="button"
            onClick={handleLineBannerClose}
          />
        </div>
      </div>
    </div>
  );
};

export default LineBanner;
